import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Trash2, Download, Eraser, Check } from "lucide-react";
import { chListFirmas, chAddFirma, chDelFirma } from "@/lib/prevelis";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SuizaFirmasModal({ doc, onClose }) {
  const fr = doc.idioma === "fr";
  const [firmas, setFirmas] = useState([]);
  const [form, setForm] = useState({ nombre: "", puesto: "", fecha: "", formador: "" });
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  const load = () => chListFirmas(doc.id).then(setFirmas).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id]);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a"; ctx.lineTo(x, y); ctx.stroke(); dirty.current = true;
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); dirty.current = false; };

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error(fr ? "Nom requis" : "Nombre requerido"); return; }
    if (!dirty.current) { toast.error(fr ? "Signature requise" : "Firma requerida"); return; }
    setSaving(true);
    try {
      const firma_base64 = canvasRef.current.toDataURL("image/png");
      await chAddFirma(doc.id, { ...form, firma_base64 });
      setForm({ nombre: "", puesto: "", fecha: "", formador: "" }); clear();
      await load();
      toast.success(fr ? "Signature enregistrée" : "Firma registrada");
    } catch { toast.error(fr ? "Erreur d'enregistrement" : "Error al guardar"); }
    finally { setSaving(false); }
  };
  const borrar = async (id) => { try { await chDelFirma(doc.id, id); await load(); } catch (e) { console.error("Error al borrar firma:", e); } };

  const inp = "h-10 w-full rounded-lg border border-slate-300 px-3 text-sm";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-3 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-5 w-full max-w-3xl my-6" onClick={(e) => e.stopPropagation()} data-testid="ch-firmas-modal">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading font-semibold text-lg">{fr ? "Enregistrer la séance / Signatures" : "Registrar impartición / Firmas"}</h3>
            <p className="text-xs text-slate-500">{doc.titulo}</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={`${BACKEND}/api/ch/documentos/${doc.id}/emargement`} target="_blank" rel="noreferrer" data-testid="ch-firmas-pdf"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
              <Download className="w-4 h-4" /> {fr ? "Feuille d'émargement" : "Hoja de firmas"}
            </a>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          <input data-testid="ch-firma-nombre" className={inp} placeholder={fr ? "Nom du travailleur *" : "Nombre del trabajador *"} value={form.nombre} onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))} />
          <input data-testid="ch-firma-puesto" className={inp} placeholder={fr ? "Poste" : "Puesto"} value={form.puesto} onChange={(e) => setForm((s) => ({ ...s, puesto: e.target.value }))} />
          <input data-testid="ch-firma-fecha" className={inp} placeholder={fr ? "Date (jj/mm/aaaa)" : "Fecha (dd/mm/aaaa)"} value={form.fecha} onChange={(e) => setForm((s) => ({ ...s, fecha: e.target.value }))} />
          <input data-testid="ch-firma-formador" className={inp} placeholder={fr ? "Formateur/responsable" : "Formador/responsable"} value={form.formador} onChange={(e) => setForm((s) => ({ ...s, formador: e.target.value }))} />
        </div>

        <p className="text-xs font-medium text-slate-600 mb-1">{fr ? "Signez ci-dessous" : "Firme aquí abajo"}</p>
        <div className="rounded-lg border border-slate-300 bg-slate-50">
          <canvas ref={canvasRef} width={680} height={180} data-testid="ch-firma-canvas"
            className="w-full touch-none rounded-lg bg-white" style={{ height: 180 }}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
        </div>
        <div className="flex items-center gap-2 mt-2">
          <button onClick={clear} data-testid="ch-firma-limpiar" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
            <Eraser className="w-4 h-4" /> {fr ? "Effacer" : "Borrar"}
          </button>
          <button onClick={guardar} disabled={saving} data-testid="ch-firma-guardar"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {fr ? "Enregistrer la signature" : "Registrar firma"}
          </button>
        </div>

        <div className="mt-4 border-t border-slate-100 pt-3">
          <p className="text-sm font-medium text-slate-800 mb-2">{fr ? "Personnes enregistrées" : "Personas registradas"} ({firmas.length})</p>
          {firmas.length === 0 ? (
            <p className="text-sm text-slate-400">{fr ? "Aucune signature pour le moment." : "Todavía no hay firmas."}</p>
          ) : (
            <div className="space-y-2" data-testid="ch-firmas-lista">
              {firmas.map((f) => (
                <div key={f.id} data-testid={`ch-firma-item-${f.id}`} className="flex items-center gap-3 border border-slate-200 rounded-lg p-2">
                  <img src={`${BACKEND}${f.url}`} alt="firma" className="h-12 w-28 object-contain bg-white border border-slate-100 rounded" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 truncate">{f.nombre}{f.puesto ? ` · ${f.puesto}` : ""}</div>
                    <div className="text-xs text-slate-500">{(f.fecha || "—")}{f.formador ? ` · ${f.formador}` : ""}</div>
                  </div>
                  <button onClick={() => borrar(f.id)} data-testid={`ch-firma-del-${f.id}`} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
